import { Category } from "@/types/category";
import { PublicServiceDetail } from "@/types/service";

import { sortPromotedServicesFirst } from "./helpers";
import {
  PROMOTION_CATEGORY_NAME,
  hasRealPromotionCategory,
  isPromotionService,
} from "./promotion-category";

export interface CategoryServiceGroup {
  id: string;
  title: string;
  services: PublicServiceDetail[];
}

export const OTHER_SERVICES_CATEGORY_NAME = "Other Services";

export const getServiceCategoryName = (service: PublicServiceDetail) =>
  service.category ?? OTHER_SERVICES_CATEGORY_NAME;

// Resolve the highest configured ancestor; invalid/cyclic links fall back to
// the service's own category so no services disappear from the menu.
export function getSidebarCategoryName(
  service: PublicServiceDetail,
  categories: Category[],
): string {
  const name = getServiceCategoryName(service);
  const byName = new Map(
    categories.map((category) => [category.name, category]),
  );
  const seen = new Set([name]);
  let current = name;
  let parent = byName.get(current)?.parentCategoryName;
  while (parent && byName.has(parent)) {
    if (seen.has(parent)) return name;
    seen.add(parent);
    current = parent;
    parent = byName.get(current)?.parentCategoryName;
  }
  return current;
}

export function getSidebarCategoryOptions(
  services: PublicServiceDetail[],
  categories: Category[] = [],
): {
  categoryNames: string[];
  useSyntheticPromotionCategory: boolean;
} {
  const useSyntheticPromotionCategory =
    services.some(isPromotionService) && !hasRealPromotionCategory(categories);
  const categoryNames = Array.from(
    new Set(
      services
        .filter((service) =>
          useSyntheticPromotionCategory ? !isPromotionService(service) : true,
        )
        .map((service) => getSidebarCategoryName(service, categories)),
    ),
  );
  if (useSyntheticPromotionCategory) {
    categoryNames.unshift(PROMOTION_CATEGORY_NAME);
  }
  return { categoryNames, useSyntheticPromotionCategory };
}

export function groupServicesByCategory(
  services: PublicServiceDetail[],
  sidebarCategory: string | undefined,
  categories: Category[],
): {
  categoryGroups: CategoryServiceGroup[];
  ungroupedServices: PublicServiceDetail[];
} {
  const groups = new Map<string, PublicServiceDetail[]>();
  const ungroupedServices: PublicServiceDetail[] = [];
  for (const service of services) {
    const name = getServiceCategoryName(service);
    if (name === sidebarCategory || !service.category) {
      ungroupedServices.push(service);
      continue;
    }
    const group = groups.get(name) ?? [];
    group.push(service);
    groups.set(name, group);
  }
  const order = new Map(
    categories.map((category) => [
      category.name,
      category.displayOrder ?? Infinity,
    ]),
  );
  return {
    categoryGroups: Array.from(groups, ([name, items]) => ({
      id: name,
      title: name,
      services: sortPromotedServicesFirst(items),
    })).sort((a, b) => {
      const difference =
        (order.get(a.title) ?? Infinity) - (order.get(b.title) ?? Infinity);
      return (
        (Number.isNaN(difference) ? 0 : difference) ||
        a.title.localeCompare(b.title, undefined, { sensitivity: "base" })
      );
    }),
    ungroupedServices: sortPromotedServicesFirst(ungroupedServices),
  };
}
